import { createContext, useContext, useEffect, useState, useCallback } from 'react'

// Three-color swatch per theme: [paper, ink (sidebar), accent]. This is what
// actually shifts per theme, so the picker preview should show all three —
// not just paper + accent (which hid the fact that sidebars all looked the
// same when they didn't have to).
//
// Linen & Ink is listed first (and set as DEFAULT_THEME below) because the
// true-black ink + near-white linen paper gives cards a noticeably higher
// contrast edge — the Cream & Clay paper field was soft enough that tiles
// read as flat.
//
// Swatches must mirror the [data-theme="..."] CSS variable values in
// pro/src/index.css. The picker tile previews paint directly from these hexes
// (not from the live --paper/--ink/--accent vars), so any time the index.css
// tokens move, update this array in the same commit or the picker will lie
// about what each theme looks like.
// eslint-disable-next-line react-refresh/only-export-components
export const THEMES = [
  // Saturated tinted papers — 2026-05-26 #3. Each theme owns its own
  // paper tint so the page background visibly shifts when the planner
  // switches themes (cream → sage → cool mist → dusty rose); card is
  // always pure #FFFFFF (set in index.css) for the consistent crisp-
  // white lift the planner asked for.
  { id: 'linen-ink',  name: 'Linen & Ink',  tagline: 'Crisp · signature clay', swatches: ['#F4F1E8', '#0A0B0E', '#B84A30'] },
  // Paper swatches round 2 (2026-05-27 PM, Option B from
  // /mocks/theme-paper-lightness-compare.html). Each value is the
  // previous round-1 swatch mixed 50/50 with white again — the planner's
  // "another 50% lighter" decision. Keep in lockstep with --paper in
  // index.css so picker tile + mini-chrome preview = real page bg.
  { id: 'cream-clay', name: 'Cream & Clay', tagline: 'Warm editorial',         swatches: ['#FAF7F1', '#14131A', '#B84A30'] },
  { id: 'cream-sage', name: 'Sage & Sand',  tagline: 'Earthy · calming',       swatches: ['#F6F8F1', '#0A140C', '#3C5832'] },
  { id: 'slate-blue', name: 'Slate & Blue', tagline: 'Cool · modern',          swatches: ['#F6F8FB', '#08101C', '#1D4ED8'] },
  { id: 'plum-dusk',  name: 'Plum & Dusk',  tagline: 'Bold · romantic',        swatches: ['#FAF4F3', '#1A0B10', '#6F1A2C'] },
]

const STORAGE_KEY = 'planos_theme'
const DEFAULT_THEME = 'linen-ink'

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {} })

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME
    const stored = localStorage.getItem(STORAGE_KEY)
    return THEMES.some(t => t.id === stored) ? stored : DEFAULT_THEME
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  const setTheme = useCallback((id) => {
    if (!THEMES.some(t => t.id === id)) return
    localStorage.setItem(STORAGE_KEY, id)
    setThemeState(id)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  return useContext(ThemeContext)
}
